<template>
    <div class="rma-quote-details">
        <div v-if="loading" class="rma-quote-details__loading" role="status">
            <v-progress-circular indeterminate size="18" width="2" color="primary" />
            Loading quote information…
        </div>
        <v-card v-if="!loading && details" class="rma-quote-details__card" outlined flat>
            <div class="rma-quote-details__eyebrow">QUOTE COST SUMMARY</div>
            <v-simple-table dense class="rma-quote-details__table">
                <template v-slot>
                    <tbody>
                        <tr v-for="(item, index) in details.items" :key="index">
                            <td>{{ item.name }}</td>
                            <td>${{ Number(item.total || 0).toFixed(2) }}</td>
                        </tr>
                        <tr>
                            <th>Subtotal</th>
                            <td>${{ Number(details.subtotal || 0).toFixed(2) }}</td>
                        </tr>
                        <tr>
                            <th>Tax</th>
                            <td>${{ Number(details.amount && details.amount.tax || 0).toFixed(2) }}</td>
                        </tr>
                        <tr>
                            <th>Total</th>
                            <td>${{ Number(details.total || 0).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
        </v-card>
        <v-alert v-if="error" type="error" dense outlined class="rma-quote-details__error" role="alert">
            Could not load paid quote information
        </v-alert>
    </div>
</template>

<script>
import Vue from 'vue'
import { paidQuoteDetails } from '@/api'

/** @typedef {{ name: string, total: number }} QuoteLineItem */
/** @typedef {{ items: QuoteLineItem[], subtotal: number, total: number, amount: { tax: number } }} QuoteDetails */

export default Vue.extend({
    name: 'ServiceDetailsQuote',
    props: {
        quoteId: {
            type: String,
            required: true
        }
    },
    /** @returns {{ loading: boolean, error: boolean, details: QuoteDetails | null }} */
    data() {
        return {
            loading: true,
            error: false,
            details: null
        }
    },
    watch: {
        quoteId: {
            immediate: true,
            handler() {
                if (this.quoteId) {
                    this.getPaidQuoteDetails()
                }
            }
        }
    },
    methods: {
        async getPaidQuoteDetails() {
            if (this.quoteId) {
                await paidQuoteDetails({ quoteId: this.quoteId })
                    .then(({ data }) => {
                        this.details = data
                    })
                    .catch((e) => {
                        console.error(e)
                        this.error = true
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        }
    }
})
</script>

<style scoped>
.rma-quote-details {
    margin-top: 8px;
}
.rma-quote-details__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    color: #aeb5c3;
    font-size: 10px;
}
.rma-quote-details__card {
    padding: 10px 12px;
    background: rgba(20, 22, 28, 0.45);
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 7px;
    box-shadow: none !important;
}
.rma-quote-details__eyebrow {
    margin-bottom: 4px;
    color: #f2a58b;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 1px;
}
.rma-quote-details__table {
    background: transparent !important;
    color: #e9edf5;
}
.rma-quote-details__table ::v-deep td,
.rma-quote-details__table ::v-deep th {
    height: 30px;
    padding: 0 5px;
    color: #c6cbd5;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    font-size: 10px;
}
.rma-quote-details__table ::v-deep th {
    color: #f1f2f5;
}
.rma-quote-details__table ::v-deep tr:last-child td {
    color: #ffb393;
    border-bottom: 0;
    font-weight: 700;
}
.rma-quote-details__error {
    margin: 8px 0 0;
    font-size: 10px;
}
</style>
